/** The globe's camera (spec §7.1; mockup tweenView, globeStep and the canvas pointer handlers): drag rotates with gentle inertia,
 * two pointers pinch and the wheel zooms, a focus spins forward in 400 ms. Reduced motion makes every move instant and drops the
 * inertia. The camera lives in a ref, so a frame redraws without a React render. */
import { useLayoutEffect, useMemo, useRef } from "react";
import { prefersReducedMotion } from "@/lib/reveal-motion";
import {
  ZOOM_MAX,
  ZOOM_MIN,
  clamp,
  lerp,
  wrapAngle,
  type Angles,
  type Camera,
} from "./globe-projection";
import type { GraphArea, Point } from "./globe-renderer";
/** Spec §7.1: a focus spins to the front in 400 ms. A press that travels no further than CLICK_SLOP px is a click (mockup). */
export const FOCUS_SPIN_MS = 400;
export const CLICK_SLOP = 4;
/** A fling spins on only when the pointer still moved this recently (mockup). */
const INERTIA_WINDOW_MS = 80;
const HALF_PI = Math.PI / 2;
const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export interface GlobePointer {
  pointerId: number;
  clientX: number;
  clientY: number;
  currentTarget: { setPointerCapture?: (pointerId: number) => void };
}
/** `toLocal` turns a pointer into canvas CSS pixels; `now` is the clock for fling timing (tests pass their own). */
export interface CameraOptions {
  toLocal: (pointer: GlobePointer) => Point;
  requestDraw: () => void;
  onTap: (point: Point) => void;
  onHover: (point: Point | null) => void;
  now?: () => number;
}
export interface GlobeCamera {
  camera: { readonly current: Camera };
  jump: (angles: Angles, zoom?: number) => void;
  turnTo: (angles: Angles, zoom?: number) => void;
  setArea: (area: GraphArea) => void;
  zoomBy: (deltaY: number) => void;
  step: (now: number) => boolean;
  handlers: {
    onPointerDown: (p: GlobePointer) => void;
    onPointerMove: (p: GlobePointer) => void;
    onPointerUp: (p: GlobePointer) => void;
    onPointerCancel: (p: GlobePointer) => void;
    onPointerLeave: () => void;
  };
}
type Pose = Angles & { zoom: number };
type Drag =
  | {
      kind: "rotate";
      start: Point;
      last: Point;
      moved: boolean;
      trail: Array<[number, number, number]>;
    }
  | { kind: "pinch"; distance: number; zoom: number };
export function useGlobeCamera(options: CameraOptions): GlobeCamera {
  const latest = useRef(options);
  useLayoutEffect(() => {
    latest.current = options;
  });
  return useMemo(() => {
    const camera = {
      current: { yaw: 0, pitch: 0, zoom: 1, cx: 0, cy: 0, radius: 1 } as Camera,
    };
    const pointers = new Map<number, Point>();
    let [spin, velocity, drag, lastStep]: [
      { from: Pose; to: Pose; start: number | null } | null,
      Angles | null,
      Drag | null,
      number,
    ] = [null, null, null, 0];
    const clock = () => latest.current.now?.() ?? performance.now();
    const set = (pose: Partial<Camera>) => {
      camera.current = {
        ...camera.current,
        ...pose,
        pitch: clamp(pose.pitch ?? camera.current.pitch, -HALF_PI, HALF_PI),
      };
    };
    const reach = () => camera.current.radius * camera.current.zoom;
    const jump = (angles: Angles, zoom = camera.current.zoom) => {
      [spin, velocity] = [null, null];
      set({ ...angles, zoom });
      latest.current.requestDraw();
    };
    const turnTo = (angles: Angles, zoom = camera.current.zoom) => {
      if (prefersReducedMotion()) return jump(angles, zoom);
      const from = {
        yaw: camera.current.yaw,
        pitch: camera.current.pitch,
        zoom: camera.current.zoom,
      };
      [velocity, spin] = [
        null,
        {
          from,
          to: {
            yaw: from.yaw + wrapAngle(angles.yaw - from.yaw),
            pitch: clamp(angles.pitch, -HALF_PI, HALF_PI),
            zoom,
          },
          start: null,
        },
      ];
      latest.current.requestDraw();
    };
    const step = (now: number) => {
      const dt = lastStep ? Math.min(48, now - lastStep) : 16;
      let moving = false;
      lastStep = now;
      if (spin) {
        spin.start ??= now;
        const t = clamp((now - spin.start) / FOCUS_SPIN_MS, 0, 1);
        const e = easeInOut(t);
        set({
          yaw: lerp(spin.from.yaw, spin.to.yaw, e),
          pitch: lerp(spin.from.pitch, spin.to.pitch, e),
          zoom: lerp(spin.from.zoom, spin.to.zoom, e),
        });
        if (t < 1) moving = true;
        else spin = null;
      }
      if (velocity && !drag) {
        set({
          yaw: camera.current.yaw + velocity.yaw * dt,
          pitch: camera.current.pitch + velocity.pitch * dt,
        });
        const decay = Math.pow(0.92, dt / 16.7);
        const next = {
          yaw: velocity.yaw * decay,
          pitch: velocity.pitch * decay,
        };
        velocity =
          Math.abs(next.yaw) + Math.abs(next.pitch) < 0.00002 ? null : next;
        if (velocity) moving = true;
      }
      if (!moving) lastStep = 0;
      return moving;
    };
    const down = (pointer: GlobePointer) => {
      pointer.currentTarget.setPointerCapture?.(pointer.pointerId);
      const point = latest.current.toLocal(pointer);
      pointers.set(pointer.pointerId, point);
      [spin, velocity] = [null, null];
      const [a, b] = [...pointers.values()];
      drag =
        a && b
          ? {
              kind: "pinch",
              distance: Math.hypot(a.x - b.x, a.y - b.y),
              zoom: camera.current.zoom,
            }
          : {
              kind: "rotate",
              start: point,
              last: point,
              moved: false,
              trail: [[clock(), point.x, point.y]],
            };
    };
    const move = (pointer: GlobePointer) => {
      const point = latest.current.toLocal(pointer);
      if (pointers.has(pointer.pointerId))
        pointers.set(pointer.pointerId, point);
      if (!drag) return latest.current.onHover(point);
      if (drag.kind === "pinch") {
        const [a, b] = [...pointers.values()];
        if (a && b && drag.distance > 0) {
          set({
            zoom: clamp(
              (drag.zoom * Math.hypot(a.x - b.x, a.y - b.y)) / drag.distance,
              ZOOM_MIN,
              ZOOM_MAX,
            ),
          });
          latest.current.requestDraw();
        }
        return;
      }
      if (
        !drag.moved &&
        Math.hypot(point.x - drag.start.x, point.y - drag.start.y) > CLICK_SLOP
      ) {
        drag.moved = true;
        latest.current.onHover(null);
      }
      if (drag.moved) {
        set({
          yaw: camera.current.yaw + (point.x - drag.last.x) / reach(),
          pitch: camera.current.pitch - (point.y - drag.last.y) / reach(),
        });
        drag.trail = [...drag.trail.slice(-5), [clock(), point.x, point.y]];
        latest.current.requestDraw();
      }
      drag.last = point;
    };
    const end = (pointer: GlobePointer, released: boolean) => {
      pointers.delete(pointer.pointerId);
      const current = drag;
      if (current?.kind === "pinch") {
        if (pointers.size === 0) drag = null;
        return;
      }
      drag = null;
      if (!current || !released) return;
      if (!current.moved)
        return latest.current.onTap(latest.current.toLocal(pointer));
      const [first, last] = [
        current.trail[0]!,
        current.trail[current.trail.length - 1]!,
      ];
      const elapsed = last[0] - first[0];
      if (
        prefersReducedMotion() ||
        elapsed <= 0 ||
        clock() - last[0] >= INERTIA_WINDOW_MS
      )
        return;
      velocity = {
        yaw: (last[1] - first[1]) / elapsed / reach(),
        pitch: -(last[2] - first[2]) / elapsed / reach(),
      };
      latest.current.requestDraw();
    };
    return {
      camera,
      jump,
      turnTo,
      step,
      setArea: (area) => set({ cx: area.cx, cy: area.cy, radius: area.radius }),
      zoomBy: (deltaY) => {
        spin = null;
        set({
          zoom: clamp(
            camera.current.zoom * Math.exp(-deltaY * 0.0015),
            ZOOM_MIN,
            ZOOM_MAX,
          ),
        });
        latest.current.requestDraw();
      },
      handlers: {
        onPointerDown: down,
        onPointerMove: move,
        onPointerUp: (pointer) => end(pointer, true),
        onPointerCancel: (pointer) => end(pointer, false),
        onPointerLeave: () => {
          if (!drag) latest.current.onHover(null);
        },
      },
    };
  }, []);
}
